import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { ProductCard } from "@/components/ProductCard";
import { CatIcon } from "@/lib/categoryIcons";
import { Footer } from "@/components/Footer";
import { LocationBar } from "@/components/LocationBar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DISTANCES = [
  { v: "any", label: "Any distance" },
  { v: "2", label: "Within 2 miles" },
  { v: "5", label: "Within 5 miles" },
  { v: "10", label: "Within 10 miles" },
];

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const { loc } = useLocation();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";
  const sort = params.get("sort") || "recent";
  const distance = params.get("distance") || "any";

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (params.get("q")) p.set("q", params.get("q"));
    if (category) p.set("category", category);
    p.set("sort", sort);
    if (loc) { p.set("lat", loc.lat); p.set("lng", loc.lng); }
    if (distance !== "any" && loc) p.set("max_distance", distance);
    api.get(`/products?${p.toString()}`).then((r) => { setProducts(r.data); setLoading(false); });
  }, [params, category, sort, distance, loc]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "any") next.delete(k); else next.set(k, v);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 min-h-[70vh]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">{category || "Browse products"}</h1>
          <p className="text-sm text-[#5A626A] mt-1 flex items-center gap-1">
            <MapPin size={13} className="text-[#E07A5F]" /> {loc ? `Near ${loc.label}` : "Set your location to see what's nearby"}
          </p>
        </div>
        <div className="sm:hidden"><LocationBar /></div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); update("q", q); }} className="relative mb-4">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A9282]" />
        <input data-testid="browse-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="What are you looking for?"
          className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-[#E5DEC9] text-sm focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/50" />
      </form>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-4">
        <button data-testid="chip-all" onClick={() => update("category", "")}
          className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${!category ? "bg-[#1A3C2A] text-white border-[#1A3C2A]" : "bg-white text-[#22252A] border-[#E5DEC9]"}`}>All</button>
        {categories.map((c) => (
          <button key={c.id} data-testid={`chip-${c.id}`} onClick={() => update("category", c.name)}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors flex items-center gap-1.5 ${category === c.name ? "bg-[#1A3C2A] text-white border-[#1A3C2A]" : "bg-white text-[#22252A] border-[#E5DEC9]"}`}>
            <CatIcon name={c.icon} size={14} /> {c.name}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SlidersHorizontal size={16} className="text-[#5A626A]" />
        <Select value={sort} onValueChange={(v) => update("sort", v)}>
          <SelectTrigger data-testid="sort-select" className="w-[170px] rounded-full bg-white border-[#E5DEC9]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Newest</SelectItem>
            <SelectItem value="distance">Nearest</SelectItem>
            <SelectItem value="price_low">Price: Low to High</SelectItem>
            <SelectItem value="price_high">Price: High to Low</SelectItem>
            <SelectItem value="rating">Top rated</SelectItem>
          </SelectContent>
        </Select>
        <Select value={distance} onValueChange={(v) => update("distance", v)}>
          <SelectTrigger data-testid="distance-select" className="w-[170px] rounded-full bg-white border-[#E5DEC9]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DISTANCES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-[#5A626A] ml-auto">{products.length} results</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] bg-white/60 rounded-2xl animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20">
          <p className="font-display text-xl font-bold text-[#1A3C2A]">No products found</p>
          <p className="text-sm text-[#5A626A] mt-1">Try a different search or widen your distance.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
      <div className="mt-16"><Footer /></div>
    </div>
  );
}
